/**
 * Push notification settings, inline in the bell's own popover (Wave 6, B4).
 *
 * Mirrors `frontend/mobile/src/app/notifications-settings.tsx` field-for-field —
 * same four toggles, same three mute durations, same reset action — against
 * the identical `/py-api/v1/push/prefs` contract, so "web + APK together"
 * (the owner's standing rule) holds for this feature on day one.
 *
 * Owner, 2026-08-28, verbatim: "mute also available sometime its annoying ..
 * mute for now, hours or day ... not more then that." No indefinite mute:
 * the three quick options below are all <= 24h, and the server rejects
 * anything longer (`backend/api/app/services/push.py::MUTE_MAX_HOURS`) even if
 * a future client tried to send one.
 */
import { CheckCircle2 } from "lucide-react";

import { useUpdatePushPrefs, useResetPushPrefs, usePushPrefs } from "./useNotifications";
import type { PushPrefsPatch } from "./api";

const ROWS: Array<{ key: "entries" | "rent" | "budget" | "backup"; label: string; hint: string }> = [
  { key: "entries", label: "Entries", hint: "Income or expense added" },
  { key: "rent", label: "Rent", hint: "Rent received, property expenses" },
  { key: "budget", label: "Budget", hint: "Threshold and overage alerts" },
  { key: "backup", label: "Backup", hint: "Nightly backup completion" },
];

const MUTE_OPTIONS: Array<{ label: string; hours: number }> = [
  { label: "1 hour", hours: 1 },
  { label: "8 hours", hours: 8 },
  { label: "Until tomorrow", hours: 24 },
];

function formatRemaining(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expiring now";
  const totalMin = Math.ceil(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function NotificationPrefsPanel() {
  const { data: prefs, isLoading } = usePushPrefs();
  const update = useUpdatePushPrefs();
  const reset = useResetPushPrefs();

  const muted = Boolean(prefs?.muteUntil && new Date(prefs.muteUntil).getTime() > Date.now());

  function patch(body: PushPrefsPatch) {
    update.mutate(body);
  }

  if (isLoading || !prefs) {
    return <p className="px-3 py-6 text-center text-xs text-[var(--cp-ink-3)]">Loading…</p>;
  }

  return (
    <div className="max-h-[24rem] overflow-y-auto px-3 py-3">
      <p className="cp-micro-label mb-2">Notify me about</p>
      <div className="mb-4 divide-y divide-[var(--cp-line-2)] rounded-md border border-[var(--cp-line-2)]">
        {ROWS.map((row) => (
          <label
            key={row.key}
            className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm"
          >
            <span>
              <span className="block font-medium text-[var(--cp-ink)]">{row.label}</span>
              <span className="block text-[11px] text-[var(--cp-ink-3)]">{row.hint}</span>
            </span>
            <input
              type="checkbox"
              checked={prefs[row.key]}
              disabled={update.isPending}
              onChange={(e) => patch({ [row.key]: e.target.checked })}
              className="h-4 w-4 accent-[var(--cp-accent)]"
            />
          </label>
        ))}
      </div>

      <p className="cp-micro-label mb-2">Mute</p>
      {muted && prefs.muteUntil ? (
        <div className="mb-3 flex items-center justify-between rounded-md border border-[var(--cp-line-2)] bg-[var(--cp-surface-2)] px-3 py-2 text-sm">
          <span className="text-[var(--cp-ink-2)]">
            Muted for {formatRemaining(prefs.muteUntil)} — resume now?
          </span>
          <button
            type="button"
            onClick={() => patch({ clearMute: true })}
            disabled={update.isPending}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold text-[var(--cp-accent-ink)] hover:bg-[var(--cp-surface)]"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Resume
          </button>
        </div>
      ) : (
        <div className="mb-3 flex flex-wrap gap-2">
          {MUTE_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              disabled={update.isPending}
              onClick={() =>
                patch({ muteUntil: new Date(Date.now() + opt.hours * 3_600_000).toISOString() })
              }
              className="rounded-full border border-[var(--cp-line)] px-3 py-1 text-[12px] font-medium text-[var(--cp-ink-2)] transition-colors hover:bg-[var(--cp-surface-2)] disabled:opacity-50"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      {update.isError && (
        <p className="mb-2 text-[11px] text-[var(--cp-rose,#ef4444)]">
          {(update.error as Error).message}
        </p>
      )}

      <button
        type="button"
        onClick={() => reset.mutate()}
        disabled={reset.isPending}
        className="w-full rounded-md border border-[var(--cp-line-2)] px-3 py-1.5 text-[12px] font-semibold text-[var(--cp-ink-3)] transition-colors hover:bg-[var(--cp-surface-2)] hover:text-[var(--cp-ink)] disabled:opacity-50"
      >
        Reset to defaults
      </button>
    </div>
  );
}

export default NotificationPrefsPanel;
